import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { NetViewTreeNodeDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { ComponentBase, Changeable } from "common";
import { DrawSettings, DashStyle, Color } from "DiscoveryEngine.TS/Topology";


@Component({
    selector: 'map-draw-settings',
	templateUrl: './map-draw-settings.component.html',
    styleUrls: ['./map-draw-settings.component.scss']
})
export class MapDrawSettingsComponent extends ComponentBase implements OnInit, OnChanges, Changeable {

	public settingsForm: UntypedFormGroup;

	public deviceDisplaySetting: UntypedFormControl;
	public shortDeviceNames: UntypedFormControl;
	public deviceTrimLeft: UntypedFormControl;
	public deviceTrimLeftChar: UntypedFormControl;
	public deviceTrimLeftCount: UntypedFormControl;
	public deviceTrimRight: UntypedFormControl;
	public deviceTrimRightChar: UntypedFormControl;
	public deviceTrimRightCount: UntypedFormControl;
	public shortIfNames: UntypedFormControl;
	public showIpOnIfNames: UntypedFormControl;

	public standardLineColor: string;
	public standardLineWidth: number;
	public standardDashStyle: string;
	public lagLineColor: string;
	public lagLineWidth: number;
	public lagDashStyle: string;
	public manualLineColor: string;
	public manualLineWidth: number;
	public manualDashStyle: string;
	public assocLineColor: string;
	public assocLineWidth: number;
	public assocDashStyle: string;
	public multiLineColor: string;
	public multiLineWidth: number;
	public multiDashStyle: string;

	@Input() public target: NetViewTreeNodeDto;
	public get hasTarget(): boolean {
		return !!this.target;
	}

	constructor(private fb: UntypedFormBuilder) {
		super();

		this.buildForm();
    }

    ngOnInit() {
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	public onLineChange(event): void {
		this.settingsForm.markAsDirty();
	}

	public updateStandardLine(): void {
		++this.standardLineWidth;
		if (this.standardLineWidth > 4)
			this.standardLineWidth = 1;
	}

	private buildForm(): void {

		let source: DrawSettings;
		if (this.hasTarget && this.target.HasMapDrawSettings) {
			source = this.target.MapDrawSettings;
		}
		else {
			source = new DrawSettings();
		}

		let deviceDisplaySetting = "" + source.DeviceDisplaySetting;
		let shortDeviceNames = source.ShortDeviceNames;
		let deviceTrimLeft = source.DeviceTrimLeft;
		let deviceTrimLeftChar = source.DeviceTrimLeftChar;
		let deviceTrimLeftCount = source.DeviceTrimLeftCount;
		let deviceTrimRight = source.DeviceTrimRight;
		let deviceTrimRightChar = source.DeviceTrimRightChar;
		let deviceTrimRightCount = source.DeviceTrimRightCount;
		let shortIfNames = source.ShortIfNames;
		let showIpOnIfNames = source.ShowIpOnIfNames;

		this.deviceDisplaySetting = new UntypedFormControl(deviceDisplaySetting);
		this.shortDeviceNames = new UntypedFormControl(shortDeviceNames);
		this.deviceTrimLeft = new UntypedFormControl(deviceTrimLeft);
		this.deviceTrimLeftChar = new UntypedFormControl(deviceTrimLeftChar, Validators.compose([Validators.required, this.singleChar()]));
		this.deviceTrimLeftCount = new UntypedFormControl(deviceTrimLeftCount, Validators.compose([Validators.required, this.minValue(1), this.maxValue(999)]));
		this.deviceTrimRight = new UntypedFormControl(deviceTrimRight);
		this.deviceTrimRightChar = new UntypedFormControl(deviceTrimRightChar, Validators.compose([Validators.required, this.singleChar()]));
		this.deviceTrimRightCount = new UntypedFormControl(deviceTrimRightCount, Validators.compose([Validators.required, this.minValue(1), this.maxValue(999)]));
		this.shortIfNames = new UntypedFormControl(shortIfNames);
		this.showIpOnIfNames = new UntypedFormControl(showIpOnIfNames);

		this.enableDisableControls();

		this.settingsForm = this.fb.group({
			deviceDisplaySetting: this.deviceDisplaySetting,
			shortDeviceNames: this.shortDeviceNames,
			deviceTrimLeft: this.deviceTrimLeft,
			deviceTrimLeftChar: this.deviceTrimLeftChar,
			deviceTrimLeftCount: this.deviceTrimLeftCount,
			deviceTrimRight: this.deviceTrimRight,
			deviceTrimRightChar: this.deviceTrimRightChar,
			deviceTrimRightCount: this.deviceTrimRightCount,
			shortIfNames: this.shortIfNames,
			showIpOnIfNames: this.showIpOnIfNames,
		});

		this.standardLineColor = source.StandardPen.Color.ToHexString();
		this.standardLineWidth = source.StandardPen.Width;
		this.standardDashStyle = DashStyle[source.StandardPen.DashStyle];
		this.lagLineColor = source.LagPen.Color.ToHexString();
		this.lagLineWidth = source.LagPen.Width;
		this.lagDashStyle = DashStyle[source.LagPen.DashStyle];
		this.manualLineColor = source.ManualPen.Color.ToHexString();
		this.manualLineWidth = source.ManualPen.Width;
		this.manualDashStyle = DashStyle[source.ManualPen.DashStyle];
		this.assocLineColor = source.AssociatedPen.Color.ToHexString();
		this.assocLineWidth = source.AssociatedPen.Width;
		this.assocDashStyle = DashStyle[source.AssociatedPen.DashStyle];
		this.multiLineColor = source.MultiPen.Color.ToHexString();
		this.multiLineWidth = source.MultiPen.Width;
		this.multiDashStyle = DashStyle[source.MultiPen.DashStyle];
	}
	
	public enableDisableControls(): void {
		this.enableDisableFormControl(this.deviceTrimLeft, this.shortDeviceNames.value);
		this.enableDisableFormControl(this.deviceTrimLeftChar, this.shortDeviceNames.value && this.deviceTrimLeft.value);
		this.enableDisableFormControl(this.deviceTrimLeftCount, this.shortDeviceNames.value && this.deviceTrimLeft.value);
		this.enableDisableFormControl(this.deviceTrimRight, this.shortDeviceNames.value);
		this.enableDisableFormControl(this.deviceTrimRightChar, this.shortDeviceNames.value && this.deviceTrimRight.value);
		this.enableDisableFormControl(this.deviceTrimRightCount, this.shortDeviceNames.value && this.deviceTrimRight.value);
	}


	// Changeable

	public get tempChanges(): boolean {
		return this.settingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.settingsForm.valid;
	}

	public get invalidTempChanges(): string[] {
		return ["Map Settings"];
	}

	public applyTempChanges(): void {
		let settings = new DrawSettings();

		settings.DeviceDisplaySetting = parseInt(this.deviceDisplaySetting.value);
		settings.ShortDeviceNames = this.shortDeviceNames.value;
		settings.DeviceTrimLeft = this.deviceTrimLeft.value;
		settings.DeviceTrimLeftChar = this.deviceTrimLeftChar.value;
		settings.DeviceTrimLeftCount = this.deviceTrimLeftCount.value;
		settings.DeviceTrimRight = this.deviceTrimRight.value;
		settings.DeviceTrimRightChar = this.deviceTrimRightChar.value;
		settings.DeviceTrimRightCount = this.deviceTrimRightCount.value;
		settings.ShortIfNames = this.shortIfNames.value;
		settings.ShowIpOnIfNames = this.showIpOnIfNames.value;

		settings.StandardPen.Color = Color.Parse(this.standardLineColor);
		settings.StandardPen.Width = this.standardLineWidth;
		settings.StandardPen.DashStyle = DashStyle[this.standardDashStyle];
		settings.LagPen.Color = Color.Parse(this.lagLineColor);
		settings.LagPen.Width = this.lagLineWidth;
		settings.LagPen.DashStyle = DashStyle[this.lagDashStyle];
		settings.ManualPen.Color = Color.Parse(this.manualLineColor);
		settings.ManualPen.Width = this.manualLineWidth;
		settings.ManualPen.DashStyle = DashStyle[this.manualDashStyle];
		settings.AssociatedPen.Color = Color.Parse(this.assocLineColor);
		settings.AssociatedPen.Width = this.assocLineWidth;
		settings.AssociatedPen.DashStyle = DashStyle[this.assocDashStyle];
		settings.MultiPen.Color = Color.Parse(this.multiLineColor);
		settings.MultiPen.Width = this.multiLineWidth;
		settings.MultiPen.DashStyle = DashStyle[this.multiDashStyle];

		this.target.MapDrawSettings = settings;
		this.target.HasChanges = true;
		this.buildForm();
	}

	public discardTempChanges(): void {
		this.buildForm();
	}
}
